<template>
  <!-- NAVIGATION DRAWER ON LEFT-->
  <v-navigation-drawer
    v-model="drawerIsOpen"
    class="elevation-1 drawer overflow-visible"
    data-locator="app-navigation"
    :permanent="permanent"
    :rail="computedMini"
    :rail-width="drawerWidths.mini"
    :width="drawerWidths.unfolded"
    order="1"
  >
   <div class="d-flex align-center column-gap-2"
        :class="computedMini ? 'py-5 px-2' : 'pa-3 pl-5'"
    >
    <div :style="{'width': logoWidthHeader}"
         :class="computedMini ? 'mx-auto' : 'py-2'"
    >
      <router-link class='d-flex'
                  style="z-index: 1"
                  :to="{ name: projectsRoute }"
                  data-locator="back-to-dashboard-button"
      >
        <img :src="appBarLogo"
            :style="{'max-width': logoWidthHeader, 'max-height': '140px'}"
            :class="{'mx-auto': computedMini}"
            alt="Logo"
            :data-locator="computedMini ? 'app-navigation-logo-mini' : 'app-navigation-logo'"
          />
      </router-link>
    </div>
     <AppTooltip v-if="!$vuetify.display.mobile" location="right">
       <template #activator="{props}">
        <span
          v-bind="props"
          style="z-index: 2"
          :class="{ 'expand-button--floating': computedMini }"
        >
          <ExpandButton :direction="computedMini ? 'right' : 'left'" @click="computedMini = !mini"/>
        </span>
       </template>
       <span>{{ $t(computedMini ? `${TRANSLATION_KEY_PREFIX}.openDrawer` : `${TRANSLATION_KEY_PREFIX}.closeDrawer`) }}</span>
     </AppTooltip>
     <div v-else class="ml-auto">
      <app-button type="icon"
        class="pa-0 ma-0"
        @click="drawerIsOpen = false"
        data-locator="close-drawer-button-mobile"
      >
        <app-icon-ds icon="close" size="sm"/>
      </app-button>
     </div>
   </div>
    <template v-if="homeLayout">
      <DocumentsSidebar class="mt-3 overflow-y-auto home-sidebar-content"
                        :class="{'mt-0 pt-0': $vuetify.display.smAndDown}"
      />
    </template>
    <template v-if="projectLayout">
      <ProjectNavigationDrawerLayout :items="projectSections"
                                     :defaultSelectedItem="selectedItem"
      />
    </template>
    <div class="d-flex align-center mt-auto drawer-footer"
        :class="[
          computedMini ? 'column-gap-1 justify-center w-100' : 'column-gap-3 pl-5',
          hasSubscriptionLogo ? '' : 'mb-2',
        ]"
    >
      <div class="d-flex align-center column-gap-2">
        <app-icon-ds icon="circle-info" size="xs"/>
        <span v-if="!computedMini" class="text-grey-500" data-locator="closd-version">
          V{{ closdVersion }}
        </span>
      </div>

      <div v-if="hasSubscriptionLogo" class="d-flex align-center my-2">
        <v-img
          alt="Closd Name"
          style="vertical-align: middle"
          :src="computedClosdLogoFooter"
          :width="logoWidthFooter"
          data-locator="closd-logo-sidebar-footer"
        />
      </div>
    </div>

  </v-navigation-drawer>
</template>

<script>
import { defineAsyncComponent, nextTick } from 'vue'
import { mapState, mapGetters, mapMutations } from 'vuex'

import closdLogo from '@/assets/img/closd_logo.svg'
import closdLogoMini from '@/assets/img/closd_logo_mini.svg'
import closdLogoMiniFooter from '@/assets/img/closd_logo_mini_footer.svg'
import AppTooltip from '@/common/AppTooltip.vue'
import ExpandButton from '@/common/ExpandButton.vue'
import { projectPages } from '@/common/navigation-drawer/roomNavigationData.js'
import { useNavigationDrawer } from '@/common/navigation-drawer/useNavigationDrawer'
import { toKebabCase } from '@/common/utils/strings.js'
import DocumentsSidebar from '@/project/documents/navigation-sidebar/DocumentsSidebar.vue'
import ProjectNavigationDrawerLayout from '@/project/navigation-drawer/ProjectNavigationDrawerLayout.vue'
import {
  QUESTION_ANSWER_ROUTE_NAME,
  ROOMS_ROUTE_NAME,
  ROOM_BIBLES_ROUTE_NAME,
  ROOM_CHECKLIST_ROUTE_NAME,
  ROOM_DOCUMENTS_ROUTE_NAME,
  ROOM_LOGS_ROUTE_NAME,
  ROOM_MEMBERS_ROUTE_NAME,
  ROOM_SETTINGS_ROUTE_NAME,
  ROOM_SIGNED_DOCUMENTS_ROUTE_NAME,
  SIGNING_CHECKLIST_ROUTE_NAME,
} from '@/router'
import { GlobalMutationTypes } from '@/store/modules/global/mutation_types'

// This duration is meant to wait after any drawer width change so that
// we can trigger a refresh to update the v-content position relative to the new drawer width
// This is due to a Vuetify bug when the navigation drawer has multiple width values set to it
// Especially when using different units
const CONTENT_REFLOW_DELAY = 190
const TRANSLATION_KEY_PREFIX = 'project.ProjectNavigationDrawer'

export default {
  name: 'ProjectNavigationDrawer',
  components: {
    AppTooltip,
    DocumentsSidebar,
    ExpandButton,
    ProjectNavigationDrawerLayout,
  },
  setup: () => {
    const { drawerWidths } = useNavigationDrawer()
    return { drawerWidths }
  },
  data () {
    return {
      selectedItem: [],
      permanent: false,
      TRANSLATION_KEY_PREFIX,
    }
  },
  computed: {
    ...mapState('room', ['currentRoom']),
    ...mapState('global', ['mini', 'homeLayout', 'projectLayout', 'toggleProjectDrawer']),
    ...mapState('user', ['profile']),
    ...mapGetters('subscription', ['getSubscriptionLogo']),
    ...mapGetters('user', ['isProtegeAskEnabled']),
    hasSubscriptionLogo () {
      return !!this.getSubscriptionLogo
    },
    appBarLogo () {
      return this.getSubscriptionLogo || (this.computedMini ? closdLogoMini : closdLogo)
    },
    computedClosdLogoFooter () {
      return this.computedMini ? closdLogoMiniFooter : closdLogo
    },
    logoWidthHeader () {
      return this.computedMini ? '40px' : '180px'
    },
    logoWidthFooter () {
      return this.computedMini ? '22px' : '67px'
    },
    drawerIsOpen: {
      get () {
        return this.$vuetify.display.mobile
          ? this.toggleProjectDrawer
          : true
      },
      set (val) {
        this.SET_TOGGLE_PROJECT_DRAWER(val)
      },
    },
    projectsRoute () {
      return ROOMS_ROUTE_NAME
    },
    aiAssistantMenuElement () {
      return {
        customComponent: defineAsyncComponent(() => import('@/common/ai-assistant/AIAssistantNavItem.vue')),
      }
    },
    homeMenuElement () {
      return this.getProjectSection(ROOMS_ROUTE_NAME)
    },
    membersMenuElement () {
      return this.getProjectSection(ROOM_MEMBERS_ROUTE_NAME)
    },
    documentsMenuElement () {
      return this.getProjectSection(ROOM_DOCUMENTS_ROUTE_NAME)
    },
    checkListMenuElement () {
      return this.getProjectSection(ROOM_CHECKLIST_ROUTE_NAME)
    },
    signingChecklistMenuElement () {
      return this.getProjectSection(SIGNING_CHECKLIST_ROUTE_NAME)
    },
    signedDocumentsMenuElement () {
      return this.getProjectSection(ROOM_SIGNED_DOCUMENTS_ROUTE_NAME)
    },
    bibleMenuElement () {
      return this.getProjectSection(ROOM_BIBLES_ROUTE_NAME)
    },
    settingsMenuElement () {
      return this.getProjectSection(ROOM_SETTINGS_ROUTE_NAME)
    },
    questionAnswersListMenuElement () {
      return this.getProjectSection(QUESTION_ANSWER_ROUTE_NAME)
    },
    logsMenuElement () {
      return this.getProjectSection(ROOM_LOGS_ROUTE_NAME)
    },
    projectSections () {
      if (this.currentRoom?.subscriptionDisabledFor) {
        return []
      }

      const menu = []
      menu.push(this.homeMenuElement)
      menu.push({title: 'separator'})
      if (this.currentRoom?.isDataroom) {
        // No need to check for room access, if the prop is truthy, we have a room
        menu.push(this.membersMenuElement)
        if (!this.currentRoom.hideDocuments) {
          menu.push(this.documentsMenuElement)
        }
        menu.push(this.questionAnswersListMenuElement)
        if (this.currentRoom.isPM) {
          menu.push(this.logsMenuElement)
        }
        menu.push(this.bibleMenuElement)

        if(this.isProtegeAskEnabled) {
          menu.push(this.aiAssistantMenuElement)
        }
        if (this.currentRoom.isPM) {
          menu.push(this.settingsMenuElement)
        }
      } else if (this.currentRoom) {
        menu.push(this.membersMenuElement)
        if (!this.currentRoom.hideDocuments) {
          menu.push(this.documentsMenuElement)
        }
        if (!this.currentRoom.hideTodo) {
          menu.push(this.checkListMenuElement)
        }

        menu.push(this.signingChecklistMenuElement, this.signedDocumentsMenuElement, this.bibleMenuElement)

        if(this.isProtegeAskEnabled) {
          menu.push(this.aiAssistantMenuElement)
        }
        if (this.currentRoom.isPM) {
          menu.push(this.settingsMenuElement)
        }
      }
      return menu
    },
    closdVersion () {
      return __APP_VERSION__
    },
    computedMini: {
      get () {
        return this.mini
      },
      set (val) {
        this.$store.commit(`global/${GlobalMutationTypes.SET_MINI}`, val)
      },
    },
  },
  watch: {
    '$vuetify.display.mobile': {
      async handler (newValue) {
        await nextTick()

        setTimeout(() => {
          this.permanent = !newValue
          if (newValue) {
            this.computedMini = false
          }
        }, CONTENT_REFLOW_DELAY)
      },
      immediate: true,
    },
    computedMini: {
      async handler () {
        if (!this.$vuetify.display.mobile) {
          await nextTick()
          setTimeout(() => {
            this.permanent = !this.permanent
          }, CONTENT_REFLOW_DELAY)
          this.permanent = !this.permanent
        }
      },
      immediate: true,
    },
  },
  mounted () {
    this.selectedItem = [this.projectSections.findIndex(el => el.key === this.$route.name)]
  },
  methods: {
    ...mapMutations('global', [GlobalMutationTypes.SET_TOGGLE_PROJECT_DRAWER]),
    getProjectSection(routeName) {
      const sectionData = projectPages[routeName]
      return {
        key: routeName,
        title: this.$t(`${TRANSLATION_KEY_PREFIX}.${sectionData.translationKey}`),
        icon: sectionData.icon,
        locatorValue: toKebabCase(sectionData.translationKey) + '-nav-item',
      }
    },
  },
}
</script>

<style scoped lang="scss">
:deep(.v-navigation-drawer__content) {
  display: flex;
  flex-direction: column;
}

.home-sidebar-content {
  // Magic number. The HappyFoxChat module is at the bottom left, at the same place as the sidebar.
  // To avoid overlapping content, we put a margin and since we use an overflow class on the content,
  // we'll be scrolling without the chat on top.
  // The chat module is around 72px of height with a container height of 90px, so this is right.
  margin-bottom: 80px
}

.expand-button--floating {
  position: absolute;
  right: -10px;
}
.drawer-footer {
  position: absolute;
  bottom: 80px;
}
</style>
